// src/components/ChatComponent.tsx
import React, { useState, useEffect, useRef } from 'react';
import { Card, Input, Button, message, Image, Space, Typography, Spin } from 'antd';
import { SendOutlined, PaperClipOutlined } from '@ant-design/icons';
import { useAuth } from '../App';
import { encryptMessage, decryptMessage, getEncryptionKey, storeEncryptionKey, generateConversationKey, EncryptionKey } from '../utils/chatEncryption';
import { chatAPI } from '../utils/api';
import { format } from 'date-fns';
import CryptoJS from 'crypto-js';
import { useIsMobile } from '../hooks/useIsMobile';

const { TextArea } = Input;
const { Text } = Typography;

interface ChatMessage {
  id: number;
  conversation_id: number;
  sender_id: number;
  encrypted_content: string;
  message_type: string;
  file_url?: string;
  created_at: string;
  decrypted_content?: string; // Optional decrypted content for display
}

interface ChatComponentProps {
  conversationId: number;
  patientId: number;
  doctorId: number | null | undefined;
  isSupport?: boolean;
  onConversationCreated?: (newConversationId: number) => void;
}

export const ChatComponent: React.FC<ChatComponentProps> = ({ conversationId, patientId, doctorId, isSupport, onConversationCreated }) => {
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [encryptionKey, setEncryptionKey] = useState<any>(null);
  const [ws, setWs] = useState<WebSocket | null>(null);
  const [currentConversationId, setCurrentConversationId] = useState<number>(conversationId);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isUnmountingRef = useRef(false);
  const prevConversationIdRef = useRef<number>(conversationId);
  /** Key last used when sending a message — try this first when decrypting (fixes web->web same-tab decryption) */
  const lastEncryptionKeyRef = useRef<EncryptionKey | null>(null);
  const pendingOptimisticIdRef = useRef<number | null>(null);
  const pendingPlaintextRef = useRef<string | null>(null);
  // Keep latest ids for WS handlers / decrypt (doctorId often arrives async after mount)
  const patientIdRef = useRef(patientId);
  const doctorIdRef = useRef(doctorId);
  const isSupportRef = useRef(isSupport);
  useEffect(() => {
    patientIdRef.current = patientId;
    doctorIdRef.current = doctorId;
    isSupportRef.current = isSupport;
  }, [patientId, doctorId, isSupport]);

  const idsReady = Boolean(isSupport) || (doctorId != null && !Number.isNaN(Number(doctorId)));

  const decryptOne = (
    encrypted: string,
    convId: number,
    pid: number,
    did: number | null | undefined,
    support: boolean | undefined
  ): string => {
    const keyToUse = getEncryptionKey(convId, pid, did, support);
    if (!keyToUse || !encrypted?.trim()) return '[Не удалось расшифровать]';
    try {
      const swappedKey =
        !support && did != null && pid !== did
          ? getEncryptionKey(convId, Number(did), Number(pid), false)
          : null;
      const decrypted = decryptMessage(
        encrypted,
        keyToUse,
        swappedKey,
        pid,
        lastEncryptionKeyRef.current,
        did ?? undefined,
        user?.id ?? undefined
      );
      return decrypted || '[Не удалось расшифровать]';
    } catch {
      return '[Не удалось расшифровать]';
    }
  };

  // Update currentConversationId when prop changes; clear last-used key ONLY if conversation actually changed
  useEffect(() => {
    const prevConvId = prevConversationIdRef.current;
    prevConversationIdRef.current = conversationId;
    setCurrentConversationId(conversationId);
    // Only clear if conversation ID actually changed (not just initialized)
    if (prevConvId !== conversationId && prevConvId !== 0) {
      console.log(`[ChatComponent] Conversation changed from ${prevConvId} to ${conversationId}, clearing lastEncryptionKeyRef`);
      lastEncryptionKeyRef.current = null;
    }
  }, [conversationId]);

  useEffect(() => {
    const convId = currentConversationId || conversationId;

    // Wait for doctorId on doctor chats so we never encrypt/decrypt with patientId:patientId fallback
    if (!idsReady) {
      return;
    }

    // Initialize encryption key only if conversation exists
    if (convId > 0) {
      const key = getEncryptionKey(convId, patientIdRef.current, doctorIdRef.current, isSupportRef.current);
      if (key) {
        storeEncryptionKey(convId, key);
        setEncryptionKey(key);
      }
    } else {
      setEncryptionKey(null);
    }

    isUnmountingRef.current = false;
    connectWebSocket();

    if (currentConversationId > 0) {
      loadMessages();
    }

    return () => {
      isUnmountingRef.current = true;
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      if (wsRef.current) {
        console.log('Cleaning up WebSocket connection');
        wsRef.current.close(1000, 'Component unmounting');
        wsRef.current = null;
        setWs(null);
      }
    };
    // Reconnect when conversation changes OR when doctorId becomes available (idsReady)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentConversationId, idsReady]);

  // Re-decrypt when doctorId arrives or after messages loaded with a stale key
  useEffect(() => {
    const convId = currentConversationId || conversationId;
    if (!convId || convId <= 0 || !idsReady) return;
    if (messages.length === 0) return;
    const pid = patientIdRef.current;
    const did = doctorIdRef.current;
    const support = isSupportRef.current;
    const keyToUse = getEncryptionKey(convId, pid, did, support);
    if (!keyToUse) return;
    storeEncryptionKey(convId, keyToUse);
    setEncryptionKey(keyToUse);
    setMessages((prev) => {
      let changed = false;
      const next = prev.map((msg) => {
        if (!msg.encrypted_content) return msg;
        const failed =
          !msg.decrypted_content ||
          msg.decrypted_content === '[Не удалось расшифровать]' ||
          msg.decrypted_content.startsWith('[Не удалось');
        if (!failed) return msg;
        const decrypted = decryptOne(msg.encrypted_content, convId, pid, did, support);
        if (decrypted !== msg.decrypted_content) changed = true;
        return { ...msg, decrypted_content: decrypted };
      });
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doctorId, isSupport, patientId, currentConversationId, messages.length, idsReady]);

  useEffect(() => {
    // Scroll to bottom when new messages arrive
    if (messages.length > 0) {
      setTimeout(() => scrollToBottom(), 100);
    }
  }, [messages.length]); // Only trigger on length change to avoid scrolling on every render

  const loadMessages = async () => {
    const convId = currentConversationId || conversationId;
    if (convId === 0) return;
    
    try {
      setLoading(true);
      const response = await chatAPI.getMessages(convId, { limit: 100 });
      // API returns messages in order: oldest first (already reversed on backend)
      // So we can use them directly - oldest first, newest last
      const messagesList = response.data as ChatMessage[];
      // Sort by created_at to ensure correct order (oldest first)
      messagesList.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
      
      // Decrypt with refs so late doctorId is not missed
      const pid = patientIdRef.current;
      const did = doctorIdRef.current;
      const support = isSupportRef.current;
      const keyToUse = getEncryptionKey(convId, pid, did, support);
      if (keyToUse) {
        storeEncryptionKey(convId, keyToUse);
        setEncryptionKey(keyToUse);
      }
      const decryptedMessages = messagesList.map(msg => {
        if (msg.decrypted_content && !String(msg.decrypted_content).startsWith('[Не удалось')) {
          return msg;
        }
        if (keyToUse && msg.encrypted_content && typeof msg.encrypted_content === 'string' && msg.encrypted_content.trim()) {
          return { ...msg, decrypted_content: decryptOne(msg.encrypted_content, convId, pid, did, support) };
        }
        return { ...msg, decrypted_content: msg.decrypted_content || '[Не удалось расшифровать]' };
      });
      
      setMessages(decryptedMessages);
      
      // Mark messages as read when viewing conversation
      try {
        await chatAPI.markMessagesRead(convId);
      } catch (error) {
        console.error('Error marking messages as read:', error);
      }
      
      // Scroll to bottom after loading
      setTimeout(() => scrollToBottom(), 100);
    } catch (error: any) {
      console.error('Error loading messages:', error);
      const status = error?.response?.status;
      const msg = status === 500
        ? 'Временная ошибка сервера. Попробуйте позже.'
        : status === 403
          ? 'Нет доступа к беседе.'
          : 'Не удалось загрузить сообщения.';
      message.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const connectWebSocket = () => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }

    // Close existing connection if any
    if (wsRef.current) {
      if (wsRef.current.readyState === WebSocket.OPEN || wsRef.current.readyState === WebSocket.CONNECTING) {
        console.log('WebSocket already connected or connecting, skipping');
        return; // Already connected or connecting
      }
      console.log('Closing existing WebSocket connection');
      wsRef.current.close();
      wsRef.current = null;
    }

    const token = localStorage.getItem('access_token');
    if (!token) return;

    // WebSocket URL - use wss for HTTPS, ws for HTTP
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    // Use currentConversationId or conversationId or 0 if creating new conversation
    const wsConversationId = currentConversationId || conversationId || 0;
    
    // Build URL with query parameters
    let wsUrl = `${protocol}//${window.location.host}/api/v1/chat/${wsConversationId}?token=${encodeURIComponent(token)}`;
    
    // If creating new conversation, add patient_id and doctor_id from refs
    if (wsConversationId === 0) {
      wsUrl += `&patient_id=${patientIdRef.current}&doctor_id=${doctorIdRef.current}`;
    }
    
    const websocket = new WebSocket(wsUrl);
    wsRef.current = websocket;

    websocket.onopen = () => {
      console.log('WebSocket connected successfully');
      setWs(websocket);
    };

    websocket.onmessage = (event) => {
      console.log('WebSocket message received:', event.data, 'type:', typeof event.data);
      try {
        let data;
        if (typeof event.data === 'string') {
          data = JSON.parse(event.data);
        } else if (event.data instanceof Blob) {
          // Handle blob data
          event.data.text().then(text => {
            const parsed = JSON.parse(text);
            handleWebSocketMessage(parsed);
          });
          return;
        } else {
          data = event.data;
        }
        console.log('Parsed WebSocket data:', data);
        handleWebSocketMessage(data);
      } catch (error) {
        console.error('Error parsing WebSocket message:', error, event.data);
      }
    };
    
    const handleWebSocketMessage = (data: any) => {
      try {
        
        // Handle conversation ready event
        if (data.type === 'conversation_ready') {
        const newConversationId = data.conversation_id;
        console.log('Conversation ready:', newConversationId, 'current conversationId:', conversationId, 'currentConversationId:', currentConversationId);
        if (newConversationId && newConversationId > 0) {
          // Update current conversation ID
          setCurrentConversationId(newConversationId);
          
          // Conversation was created or exists, generate shared encryption key
          // Both users will generate the same key for the same conversation ID
          const newKey = generateConversationKey(
            newConversationId,
            patientIdRef.current,
            doctorIdRef.current,
            isSupportRef.current
          );
          storeEncryptionKey(newConversationId, newKey);
          setEncryptionKey(newKey);
          
          // Always load messages when conversation is ready
          loadMessagesForConversation(newConversationId);
          // Notify parent component to update URL
          if (onConversationCreated && (conversationId === 0 || currentConversationId === 0)) {
            onConversationCreated(newConversationId);
          }
        }
        return;
      }
      
      // Send ack may include full message payload (id + conversation_id) so sender can append.
      // Also handles peer broadcast messages the same way.
      console.log('Checking if message data:', data.id, data.conversation_id, data.status, data);
      if (data.id && data.conversation_id) {
        console.log('Received message via WebSocket:', data.id, data.sender_id);
        
        // Get current encryption key — use refs so async doctorId is not stale in WS closure
        const convId = data.conversation_id;
        const pid = patientIdRef.current;
        const did = doctorIdRef.current;
        const support = isSupportRef.current;
        const keyToUse = getEncryptionKey(convId, pid, did, support);
        if (keyToUse) {
          // Update state if needed
          if (!encryptionKey || encryptionKey.key !== keyToUse.key) {
            storeEncryptionKey(convId, keyToUse);
            setEncryptionKey(keyToUse);
          }
        }
        
        // Check if message already exists to avoid duplicates; replace optimistic temp messages
        setMessages(prev => {
          const exists = prev.some(msg => msg.id === data.id);
          if (exists) {
            console.log('Message already exists, skipping:', data.id);
            return prev;
          }

          const optimisticPlaintext = pendingPlaintextRef.current;
          const withoutOptimistic = prev.filter((msg) => {
            if (pendingOptimisticIdRef.current != null && msg.id === pendingOptimisticIdRef.current) {
              return false;
            }
            return true;
          });
          if (pendingOptimisticIdRef.current != null) {
            pendingOptimisticIdRef.current = null;
            pendingPlaintextRef.current = null;
          }
          
          // If message already has decrypted_content, use it
          if (data.decrypted_content) {
            const newMessage = { ...data };
            const updated = [...withoutOptimistic, newMessage];
            updated.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
            setTimeout(() => scrollToBottom(), 100);
            return updated;
          }
          
          // Decrypt and add message (pass swapped key for iOS)
          // For support chat, don't use swapped key (iOS uses patientId:doctorId where doctorId is admin ID)
          // CRITICAL FIX: For own messages (just sent), use lastEncryptionKeyRef as the PRIMARY key - it's the exact key we used to encrypt
          if (data.encrypted_content && typeof data.encrypted_content === 'string' && data.encrypted_content.trim()) {
            try {
              const isOwnMessage = data.sender_id === user?.id;
              // For own messages, use the exact key we used when sending (lastEncryptionKeyRef)
              // This fixes web->web decryption where closure might have stale patientId/doctorId
              const primaryKey: EncryptionKey = (isOwnMessage && lastEncryptionKeyRef.current) ? lastEncryptionKeyRef.current : (keyToUse as EncryptionKey);
              const swappedKey = (!support && did != null && pid !== did) ? getEncryptionKey(convId, Number(did), Number(pid), false) : null;
              
              console.error(`[ChatComponent] DECRYPT: msgId=${data.id}, sender=${data.sender_id}, isOwn=${isOwnMessage}, primaryKey=${primaryKey.key}, lastRef=${lastEncryptionKeyRef.current ? lastEncryptionKeyRef.current.key : 'null'}`);
              
              // Prefer plaintext we just typed for own messages
              const plaintextHint = isOwnMessage && optimisticPlaintext ? optimisticPlaintext : undefined;
              const decrypted =
                plaintextHint ||
                decryptMessage(data.encrypted_content, primaryKey, swappedKey, pid, lastEncryptionKeyRef.current, did ?? undefined, user?.id ?? undefined) ||
                '';
              const newMessage = { ...data, decrypted_content: decrypted || '[Не удалось расшифровать]' };
              const updated = [...withoutOptimistic, newMessage];
              // Sort by created_at to maintain order
              updated.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
              // Scroll to bottom after adding message
              setTimeout(() => scrollToBottom(), 100);
              return updated;
            } catch (error) {
              console.error('Decryption error in WebSocket:', error, data.id);
              // Try old key method for backward compatibility
              try {
                const oldSecret = localStorage.getItem('access_token') || 'default_secret';
                const oldKeyMaterial = `${pid}:${did}:${oldSecret}`;
                const oldKeyHash = CryptoJS.SHA256(oldKeyMaterial);
                const oldKeyHex = oldKeyHash.toString();
                const signingKeyHex = oldKeyHex.substring(0, 32);
                const encryptionKeyHex = oldKeyHex.substring(32, 64);
                const oldKeyObj: EncryptionKey = {
                  key: '',
                  signingKey: CryptoJS.enc.Hex.parse(signingKeyHex),
                  encryptionKey: CryptoJS.enc.Hex.parse(encryptionKeyHex)
                };
                const decrypted = decryptMessage(data.encrypted_content, oldKeyObj, null, patientId, lastEncryptionKeyRef.current, doctorId ?? undefined, user?.id ?? undefined);
                const newMessage = { ...data, decrypted_content: decrypted };
                const updated = [...withoutOptimistic, newMessage];
                updated.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
                setTimeout(() => scrollToBottom(), 100);
                return updated;
              } catch (oldError) {
                console.error('Error decrypting with old key in WebSocket:', oldError);
                const newMessage = { ...data, decrypted_content: '[Не удалось расшифровать]' };
                const updated = [...withoutOptimistic, newMessage];
                updated.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
                setTimeout(() => scrollToBottom(), 100);
                return updated;
              }
            }
          } else {
            // Message doesn't have encrypted_content or it's empty
            console.warn('Message missing encrypted_content or it\'s empty:', data.id, 'encrypted_content type:', typeof data.encrypted_content, 'decrypted_content:', data.decrypted_content ? 'present' : 'missing');
            // If message has decrypted_content, use it; otherwise mark as error
            const newMessage = { ...data, decrypted_content: data.decrypted_content || '[Сообщение без зашифрованного содержимого]' };
            const updated = [...withoutOptimistic, newMessage];
            updated.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
            setTimeout(() => scrollToBottom(), 100);
            return updated;
          }
        });
      } else if (data.status === 'sent') {
        // Legacy ack without message payload — keep optimistic bubble if present
        console.log('Message sent confirmation received (no payload)');
      } else {
        console.log('Message data missing required fields:', data);
      }
      } catch (error) {
        console.error('Error handling WebSocket message:', error, data);
      }
    };

    websocket.onerror = (error) => {
      console.error('WebSocket error:', error);
      // Only notify if we never established a connection (avoids noise on tab close / reconnect)
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
        message.error('Ошибка подключения к чату');
      }
    };

    websocket.onclose = (event) => {
      console.log('WebSocket disconnected', event.code, event.reason, 'wasClean:', event.wasClean);
      console.trace('WebSocket close stack trace');
      wsRef.current = null;
      setWs(null);
      if (isUnmountingRef.current) {
        return;
      }
      // Always try to reconnect if conversation exists
      // Normal closure (1000) might happen due to server restart or network issues
      const convId = currentConversationId || conversationId;
      if (convId > 0) {
        console.log('Reconnecting WebSocket in 2 seconds...');
        reconnectTimerRef.current = setTimeout(() => {
          const currentConvId = currentConversationId || conversationId;
          if (currentConvId > 0 && (!wsRef.current || wsRef.current.readyState === WebSocket.CLOSED)) {
            console.log('Attempting to reconnect WebSocket...');
            connectWebSocket();
          }
        }, 2000);
      }
    };
  };

  const loadMessagesForConversation = async (convId: number) => {
    try {
      setLoading(true);
      const response = await chatAPI.getMessages(convId, { limit: 100 });
      // API returns messages in order: oldest first (already reversed on backend)
      // So we can use them directly - oldest first, newest last
      const messagesList = response.data as ChatMessage[];
      // Sort by created_at to ensure correct order (oldest first)
      messagesList.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
      
      // Always derive key from refs so async doctorId is not stale
      const pid = patientIdRef.current;
      const did = doctorIdRef.current;
      const support = isSupportRef.current;
      const keyToUse = getEncryptionKey(convId, pid, did, support);
      if (keyToUse) {
        storeEncryptionKey(convId, keyToUse);
        setEncryptionKey(keyToUse);
      }

      const decryptedMessages = messagesList.map(msg => {
        if (msg.decrypted_content && !String(msg.decrypted_content).startsWith('[Не удалось')) {
          return msg;
        }
        if (keyToUse && msg.encrypted_content && typeof msg.encrypted_content === 'string' && msg.encrypted_content.trim()) {
          return { ...msg, decrypted_content: decryptOne(msg.encrypted_content, convId, pid, did, support) };
        }
        return { ...msg, decrypted_content: msg.decrypted_content || '[Не удалось расшифровать]' };
      });
      
      setMessages(decryptedMessages);
      
      try {
        await chatAPI.markMessagesRead(convId);
      } catch (error) {
        console.error('Error marking messages as read:', error);
      }
      
      setTimeout(() => scrollToBottom(), 100);
    } catch (error) {
      console.error('Error loading messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async () => {
    const convId = currentConversationId || conversationId;
    
    if (!inputMessage.trim()) {
      return;
    }
    
    if (convId === 0) {
      message.warning('Беседа еще не создана. Пожалуйста, подождите...');
      return;
    }

    if (!isSupport && (doctorId == null || doctorId === undefined)) {
      message.warning('Данные врача ещё загружаются. Подождите секунду и отправьте снова.');
      return;
    }
    
    // Always generate the correct key with isSupport flag (don't rely on state)
    const keyToUse = getEncryptionKey(convId, patientId, doctorId, isSupport);
    if (!keyToUse) {
      message.error('Ключ шифрования не готов. Пожалуйста, подождите...');
      return;
    }
    
    // Update state if needed
    if (!encryptionKey || encryptionKey.key !== keyToUse.key) {
      storeEncryptionKey(convId, keyToUse);
      setEncryptionKey(keyToUse);
    }
    
    // Debug logging
    console.log(`[ChatComponent] 🔐 SENDING message: convId=${convId}, patientId=${patientId}, doctorId=${doctorId}, isSupport=${isSupport}, key=${keyToUse.key.substring(0, 20)}... (full key: ${keyToUse.key})`);
    
    const activeWs = wsRef.current ?? ws;
    if (!activeWs || activeWs.readyState !== WebSocket.OPEN) {
      message.error('WebSocket не подключен. Пожалуйста, подождите...');
      console.error('WebSocket state:', activeWs ? activeWs.readyState : 'null');
      return;
    }

    try {
      setSending(true);
      
      // Encrypt message with the correct key (includes isSupport flag)
      const encrypted = encryptMessage(inputMessage, keyToUse);
      lastEncryptionKeyRef.current = keyToUse;
      console.log(`[ChatComponent] 💾 Saved lastEncryptionKeyRef: ${keyToUse.key} (will use this first when decrypting)`);

      // Send via WebSocket
      const messageToSend = {
        type: 'text',
        content: encrypted,
      };
      console.log('Sending message via WebSocket:', messageToSend.type, 'WebSocket state:', activeWs.readyState, 'URL:', activeWs.url);
      
      // Check if WebSocket is still open
      if (activeWs.readyState !== WebSocket.OPEN) {
        console.error('WebSocket is not open! State:', activeWs.readyState);
        message.error('WebSocket соединение закрыто. Переподключение...');
        connectWebSocket();
        return;
      }
      
      try {
        activeWs.send(JSON.stringify(messageToSend));
        console.log('Message sent successfully, WebSocket state after send:', activeWs.readyState);

        // Optimistic UI: show message immediately; replace with server message on ack/broadcast
        const optimisticId = -Date.now();
        const plaintext = inputMessage;
        pendingOptimisticIdRef.current = optimisticId;
        pendingPlaintextRef.current = plaintext;
        setMessages((prev) => [
          ...prev,
          {
            id: optimisticId,
            conversation_id: convId,
            sender_id: user?.id ?? 0,
            encrypted_content: encrypted,
            message_type: 'text',
            created_at: new Date().toISOString(),
            decrypted_content: plaintext,
          },
        ]);
        setTimeout(() => scrollToBottom(), 50);
        
        // Check state after a short delay
        setTimeout(() => {
          console.log('WebSocket state 100ms after send:', wsRef.current?.readyState ?? ws?.readyState);
        }, 100);
      } catch (error) {
        console.error('Error sending message:', error);
        message.error('Ошибка отправки сообщения');
      }

      setInputMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
      message.error('Ошибка отправки сообщения');
    } finally {
      setSending(false);
    }
  };

  const handleFileUpload = async (file: File) => {
    const convId = currentConversationId || conversationId;
    if (convId === 0) {
      message.error('Дождитесь создания беседы перед отправкой файла');
      return;
    }
    
    const activeWs = wsRef.current ?? ws;
    if (!activeWs || activeWs.readyState !== WebSocket.OPEN) {
      message.error('WebSocket не подключен. Пожалуйста, подождите...');
      return;
    }
    
    try {
      setSending(true);
      
      // Upload file
      const formData = new FormData();
      formData.append('file', file);

      const response = await chatAPI.uploadFile(convId, formData);
      const { file_url, message_type } = response.data as { file_url: string; message_type: string };

      // Ensure we have the correct encryption key (with isSupport flag)
      let keyToUse = encryptionKey;
      if (!keyToUse) {
        keyToUse = getEncryptionKey(convId, patientId, doctorId, isSupport);
        if (keyToUse) {
          storeEncryptionKey(convId, keyToUse);
          setEncryptionKey(keyToUse);
        }
      }
      
      if (!keyToUse) {
        message.error('Ключ шифрования не готов. Пожалуйста, подождите...');
        return;
      }
      
      // Encrypt file description (just file name for reference)
      const fileDescription = file.name;
      const encrypted = encryptMessage(fileDescription, keyToUse);
      lastEncryptionKeyRef.current = keyToUse;

      // Send via WebSocket
      activeWs.send(JSON.stringify({
        type: message_type,
        content: encrypted,
        file_url: file_url,
      }));

      // Don't add temporary message - it will come back via WebSocket with proper ID
      // This prevents duplication
      
      message.success('Файл отправлен');
    } catch (error) {
      console.error('Error uploading file:', error);
      message.error('Ошибка загрузки файла');
    } finally {
      setSending(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Helper function to normalize file URLs (convert old localhost:9000 URLs to API endpoint format)
  const normalizeFileUrl = (url: string | undefined): string | undefined => {
    if (!url) return url;
    
    // Get auth token for API requests
    const token = localStorage.getItem('access_token');
    
    // Convert old http://localhost:9000/... URLs to API endpoint format
    if (url.startsWith('http://localhost:9000/')) {
      // Extract bucket/key from URL: http://localhost:9000/bucket-name/path/to/file
      const path = url.replace('http://localhost:9000/', '');
      return `${window.location.origin}/api/v1/files/preview?file_url=${encodeURIComponent(path)}${token ? `&token=${token}` : ''}`;
    }
    
    // If URL is API endpoint format, add token if needed
    if (url.startsWith('/api/v1/files/preview')) {
      const separator = url.includes('?') ? '&' : '?';
      return `${window.location.origin}${url}${token ? `${separator}token=${token}` : ''}`;
    }
    
    // If URL is relative and doesn't start with /api/, add origin
    if (!url.startsWith('http') && !url.startsWith('/api/')) {
      return `${window.location.origin}${url}`;
    }
    
    // If URL is relative and starts with /api/, add origin
    if (url.startsWith('/api/')) {
      return `${window.location.origin}${url}`;
    }
    
    // For absolute URLs, return as is
    return url;
  };

  const renderMessage = (msg: ChatMessage) => {
    const isOwn = msg.sender_id === user?.id;
    let content = msg.decrypted_content;
    const needsDecrypt =
      !content ||
      content === '[Не удалось расшифровать]' ||
      content.startsWith('[Не удалось');

    if (needsDecrypt && msg.encrypted_content && typeof msg.encrypted_content === 'string' && msg.encrypted_content.trim()) {
      const convId = conversationId || currentConversationId || 0;
      content = decryptOne(
        msg.encrypted_content,
        convId,
        patientIdRef.current,
        doctorIdRef.current,
        isSupportRef.current
      );
    }
    
    if (!content) {
      content = '[Не удалось расшифровать]';
    }

    return (
      <div
        key={msg.id}
        style={{
          display: 'flex',
          justifyContent: isOwn ? 'flex-end' : 'flex-start',
          marginBottom: '12px',
          width: '100%',
          boxSizing: 'border-box'
        }}
      >
        <Card
          size="small"
          style={{
            maxWidth: '70%',
            backgroundColor: isOwn ? '#7CB342' : '#f0f0f0',
            borderRadius: '12px',
            wordWrap: 'break-word',
            wordBreak: 'break-word',
            overflowWrap: 'break-word'
          }}
          bodyStyle={{ padding: '8px 12px' }}
        >
          {/* Show file if present, otherwise show text content */}
          {msg.file_url ? (
            <div>
              {msg.message_type === 'image' ? (
                <Image 
                  src={normalizeFileUrl(msg.file_url) || ''} 
                  alt="Изображение" 
                  style={{ maxWidth: '200px', borderRadius: '4px', display: 'block' }}
                  preview={true}
                />
              ) : (
                <a 
                  href={normalizeFileUrl(msg.file_url) || ''} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  download
                  style={{ 
                    color: isOwn ? '#fff' : '#42A5F5',
                    textDecoration: 'none',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '4px 8px',
                    backgroundColor: isOwn ? 'rgba(255,255,255,0.2)' : 'rgba(24,144,255,0.1)',
                    borderRadius: '4px',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = isOwn ? 'rgba(255,255,255,0.3)' : 'rgba(24,144,255,0.2)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = isOwn ? 'rgba(255,255,255,0.2)' : 'rgba(24,144,255,0.1)';
                  }}
                >
                  <PaperClipOutlined /> {content && !content.startsWith('Файл:') ? content : 'Скачать файл'}
                </a>
              )}
            </div>
          ) : (
            <Text style={{ 
              color: isOwn ? '#fff' : '#000',
              wordWrap: 'break-word',
              wordBreak: 'break-word',
              overflowWrap: 'break-word',
              whiteSpace: 'pre-wrap'
            }}>{content && !content.startsWith('Файл:') ? content : ''}</Text>
          )}
          <div style={{ marginTop: '4px', fontSize: '10px', opacity: 0.7 }}>
            {format(new Date(msg.created_at), 'HH:mm')}
          </div>
        </Card>
      </div>
    );
  };

  return (
    <Card 
      style={{ 
        borderRadius: '12px', 
        height: '600px', 
        display: 'flex', 
        flexDirection: 'column',
        overflow: 'hidden',
        padding: 0
      }}
      bodyStyle={{ 
        padding: 0, 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        overflow: 'hidden'
      }}
    >
      {/* Messages */}
      <div 
        style={{ 
          flex: 1, 
          overflowY: 'auto', 
          overflowX: 'hidden',
          padding: '16px', 
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0
        }}
      >
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Spin />
          </div>
        ) : messages.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Text type="secondary">Нет сообщений. Начните общение!</Text>
          </div>
        ) : (
          <>
            {/* Messages displayed from oldest to newest, newest at bottom */}
            {messages.map(renderMessage)}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Input */}
      <div style={{ 
        padding: '12px 16px', 
        borderTop: '1px solid #f0f0f0',
        flexShrink: 0,
        width: '100%',
        boxSizing: 'border-box'
      }}>
        <Space.Compact style={{ width: '100%', display: 'flex', alignItems: 'flex-end' }}>
          <input
            ref={fileInputRef}
            type="file"
            style={{ display: 'none' }}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) {
                handleFileUpload(file);
              }
            }}
          />
          <Button
            icon={<PaperClipOutlined />}
            onClick={() => fileInputRef.current?.click()}
            disabled={sending}
            style={{ 
              flexShrink: 0,
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          />
          <TextArea
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            placeholder="Введите сообщение..."
            autoSize={{ minRows: isMobile ? 2 : 1, maxRows: 4 }}
            onPressEnter={(e) => {
              if (!e.shiftKey) {
                e.preventDefault();
                sendMessage();
              }
            }}
            style={{ flex: 1, minWidth: 0 }}
          />
          <Button
            type="primary"
            icon={<SendOutlined />}
            onClick={sendMessage}
            loading={sending}
            disabled={!inputMessage.trim()}
            style={{ 
              backgroundColor: '#7CB342', 
              borderColor: '#7CB342',
              flexShrink: 0,
              minWidth: isMobile ? '40px' : 'auto'
            }}
          >
            {!isMobile && 'Отправить'}
          </Button>
        </Space.Compact>
      </div>
    </Card>
  );
};
